import { IDENTITY_FIELDS } from '@/features/entity-resolution/identityAssessment'
import type { MergeExactJob, MergeExactResult } from '@/services/api/entity-resolution-groups'
import {
  isResolutionCount as count,
  isResolutionRecord as record,
  isResolutionScore as score,
} from './resolution-values'

export interface ResolutionMergeRules {
  entity_type: 'Person' | 'Company' | 'Organization' | null
  min_similarity: number
  max_similarity: number
  required_matching_fields: string[]
  same_source: boolean
}

export interface ResolutionRulePlan {
  case_id: string
  preview_id: string
  rules: ResolutionMergeRules
  total_groups: number
  matched_subgroups: number
  affected_groups: number
  selected_members: number
  merge_operations: number
  left_out_members: number
  rejected_groups: number
  score_basis: string
}

function toolPayload(
  result: unknown,
  artifact: unknown,
  key: string
): Record<string, unknown> | null {
  try {
    const payload = record(artifact) ? (artifact[key] ?? result) : result
    const value: unknown = typeof payload === 'string' ? JSON.parse(payload) : payload
    return record(value) && value.status !== 'error' ? value : null
  } catch {
    return null
  }
}

function isRules(value: unknown): value is ResolutionMergeRules {
  return (
    record(value) &&
    (value.entity_type === null ||
      (typeof value.entity_type === 'string' &&
        ['Person', 'Company', 'Organization'].includes(value.entity_type))) &&
    score(value.min_similarity) &&
    score(value.max_similarity) &&
    value.min_similarity <= value.max_similarity &&
    typeof value.same_source === 'boolean' &&
    Array.isArray(value.required_matching_fields) &&
    value.required_matching_fields.every(
      field => typeof field === 'string' && IDENTITY_FIELDS.some(known => known === field)
    )
  )
}

export function parseResolutionRulePlan(
  result: unknown,
  artifact: unknown,
  caseId: string
): ResolutionRulePlan | null {
  const value = toolPayload(result, artifact, 'resolution_rule_plan')
  if (
    !caseId ||
    !value ||
    value.case_id !== caseId ||
    typeof value.preview_id !== 'string' ||
    !value.preview_id ||
    !isRules(value.rules) ||
    typeof value.score_basis !== 'string' ||
    !count(value.total_groups) ||
    !count(value.matched_subgroups) ||
    !count(value.affected_groups) ||
    !count(value.selected_members) ||
    !count(value.merge_operations) ||
    !count(value.left_out_members) ||
    !count(value.rejected_groups)
  )
    return null
  return {
    case_id: caseId,
    preview_id: value.preview_id,
    rules: value.rules,
    total_groups: value.total_groups,
    matched_subgroups: value.matched_subgroups,
    affected_groups: value.affected_groups,
    selected_members: value.selected_members,
    merge_operations: value.merge_operations,
    left_out_members: value.left_out_members,
    rejected_groups: value.rejected_groups,
    score_basis: value.score_basis,
  }
}

function progress(value: unknown): MergeExactResult | null {
  if (
    !record(value) ||
    !count(value.groups_total) ||
    !count(value.groups_processed) ||
    !count(value.merged_groups) ||
    !count(value.merged_members) ||
    !count(value.skipped_groups) ||
    !count(value.failed_groups)
  )
    return null
  return {
    groups_total: value.groups_total,
    groups_processed: value.groups_processed,
    merged_groups: value.merged_groups,
    merged_members: value.merged_members,
    skipped_groups: value.skipped_groups,
    failed_groups: value.failed_groups,
    last_error: typeof value.last_error === 'string' ? value.last_error : null,
  }
}

export function parseResolutionRuleJob(
  result: unknown,
  artifact: unknown,
  caseId: string
): MergeExactJob | null {
  const value = toolPayload(result, artifact, 'resolution_rule_job')
  if (
    !caseId ||
    !value ||
    value.case_id !== caseId ||
    typeof value.job_id !== 'string' ||
    !value.job_id ||
    (value.status !== 'running' && value.status !== 'completed' && value.status !== 'failed') ||
    typeof value.started_at !== 'string' ||
    typeof value.updated_at !== 'string' ||
    !(value.finished_at === null || typeof value.finished_at === 'string') ||
    !(value.error === null || typeof value.error === 'string')
  )
    return null
  return {
    case_id: caseId,
    job_id: value.job_id,
    status: value.status,
    started_at: value.started_at,
    updated_at: value.updated_at,
    finished_at: typeof value.finished_at === 'string' ? value.finished_at : null,
    error: typeof value.error === 'string' ? value.error : null,
    result: progress(value.result),
    progress: progress(value.progress),
  }
}
